import React, { useState, useEffect, useMemo } from 'react';

// Rooms equipped with ultra-widescreen monitors
const ULTRAWIDE_ROOMS = ['O2', 'O3', 'C3', 'C6', 'C11', 'C14', 'C16', 'C20'];

// SSP Brand Colors mapped to room types
const TYPE_COLORS = {
  conference: { available: '#193d68', booked: '#5D5E60', hover: '#2a5a94' },  // Dark Navy
  meeting:    { available: '#193d68', booked: '#5D5E60', hover: '#2a5a94' },  // Dark Navy
  huddle:     { available: '#193d68', booked: '#5D5E60', hover: '#2a5a94' },  // Dark Navy
  office:     { available: '#47ACE4', booked: '#5D5E60', hover: '#6fc0ec' },  // Bright Blue
  cubicle:    { available: '#47ACE4', booked: '#5D5E60', hover: '#6fc0ec' },  // Bright Blue
};

const TYPE_LABELS = {
  conference: 'Conference Room',
  meeting: 'Meeting Room',
  huddle: 'Huddle Room',
  office: 'Office',
  cubicle: 'Cubicle',
};

/**
 * FloorPlan component - renders an interactive SVG map of any office.
 * All room data and layout coordinates come from the officeConfig prop,
 * so the same component works for every office location.
 */
export default function FloorPlan({ officeConfig, availability = {}, onRoomSelect, selectedRoom, isKiosk = false }) {
  const [hoveredRoom, setHoveredRoom] = useState(null);

  const rooms = officeConfig.rooms;
  const fixedAreas = officeConfig.fixedAreas || [];

  const getRoomColor = (room) => {
    const colors = TYPE_COLORS[room.type] || TYPE_COLORS.office;
    const isBooked = availability[room.id]?.currentlyBooked;
    if (isBooked) return colors.booked;
    if (hoveredRoom === room.id) return colors.hover;
    if (selectedRoom === room.id) return colors.hover;
    return colors.available;
  };

  const hasFloorplanImage = !!officeConfig.floorplanImage;

  const getRoomOpacity = (room) => {
    const isBooked = availability[room.id]?.currentlyBooked;
    if (hasFloorplanImage) {
      // High opacity so rooms are clearly visible over the light gray floorplan
      if (hoveredRoom === room.id || selectedRoom === room.id) return 1;
      return 0.95;
    }
    if (isBooked) return 0.5;
    if (hoveredRoom === room.id || selectedRoom === room.id) return 0.9;
    return 0.7;
  };

  const availableCubicleCount = useMemo(() => {
    return rooms.filter(r => r.type === 'cubicle' && r.bookable !== false && !availability[r.id]?.currentlyBooked).length;
  }, [rooms, availability]);

  const availableOfficeCount = useMemo(() => {
    return rooms.filter(r => r.type === 'office' && r.bookable !== false && !availability[r.id]?.currentlyBooked).length;
  }, [rooms, availability]);

  // Determine which room types exist in this office for the legend
  const activeTypes = useMemo(() => {
    const types = new Set(rooms.map(r => r.type));
    return Object.keys(TYPE_LABELS).filter(t => types.has(t));
  }, [rooms]);

  return (
    <div className="floorplan-container">
      <div className="floorplan-header">
        <h2>{isKiosk ? 'MKE - Floor Plan' : 'Book a Space'}</h2>
        <div className="legend">
          <span className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#193d68' }} />
            Conference / Meeting / Huddle
          </span>
          <span className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#47ACE4' }} />
            Office / Cubicle
          </span>
          <span className="legend-item">
            <span className="legend-dot" style={{ backgroundColor: '#5D5E60' }} />
            Booked
          </span>
        </div>
      </div>

      {/* Quick-book buttons (only show if the types exist in this office) */}
      <div className="quick-book">
        {rooms.some(r => r.type === 'cubicle') && (
          <button
            className="quick-book-btn cubicle-btn"
            onClick={() => onRoomSelect?.('any-cubicle')}
            disabled={availableCubicleCount === 0}
          >
            Any Available Cubicle ({availableCubicleCount} open)
          </button>
        )}
        {rooms.some(r => r.type === 'office') && (
          <button
            className="quick-book-btn office-btn"
            onClick={() => onRoomSelect?.('any-office')}
            disabled={availableOfficeCount === 0}
          >
            Any Available Office ({availableOfficeCount} open)
          </button>
        )}
      </div>

      <svg
        viewBox={officeConfig.svgViewBox}
        className="floorplan-svg"
        role="img"
        aria-label={`${officeConfig.name} office floor plan`}
      >
        {/* Light-gray architectural floor plan as background */}
        {officeConfig.floorplanImage && (
          <defs>
            <filter id="lighten-floorplan">
              <feColorMatrix type="saturate" values="0" />
              <feComponentTransfer>
                <feFuncR type="linear" slope="0.35" intercept="0.65" />
                <feFuncG type="linear" slope="0.35" intercept="0.65" />
                <feFuncB type="linear" slope="0.35" intercept="0.65" />
              </feComponentTransfer>
            </filter>
          </defs>
        )}
        {officeConfig.floorplanImage ? (
          <image
            href={officeConfig.floorplanImage}
            x="0" y="0"
            width={officeConfig.floorplanSize?.w || officeConfig.svgViewBox.split(' ')[2]}
            height={officeConfig.floorplanSize?.h || officeConfig.svgViewBox.split(' ')[3]}
            preserveAspectRatio="xMidYMid meet"
            filter="url(#lighten-floorplan)"
          />
        ) : (
          // Fallback: plain rectangle if no floorplan image
          (() => {
            const [, , vw, vh] = officeConfig.svgViewBox.split(' ').map(Number);
            return <rect x="0" y="0" width={vw} height={vh} fill="#f8f9fa" stroke="#333" strokeWidth="3" rx="4" />;
          })()
        )}

        {/* Fixed / non-bookable areas - only show if no floorplan image (already visible in the drawing) */}
        {!officeConfig.floorplanImage && fixedAreas.map((area) => (
          <g key={area.label}>
            <rect
              x={area.x} y={area.y} width={area.w} height={area.h}
              fill="#e0e0e0" stroke="#999" strokeWidth="1" rx="2"
            />
            <text
              x={area.x + area.w / 2} y={area.y + area.h / 2}
              textAnchor="middle" dominantBaseline="central"
              fontSize="11" fill="#666" fontWeight="500"
            >
              {area.label}
            </text>
          </g>
        ))}

        {/* Bookable rooms */}
        {rooms.filter(r => r.bookable !== false).map((room) => {
          const f = room.floor;
          const isBooked = availability[room.id]?.currentlyBooked;
          const isCubicle = room.type === 'cubicle';
          const fontSize = isCubicle ? 10 : 12;
          const isOffice = room.type === 'office';
          const hasUltrawide = ULTRAWIDE_ROOMS.includes(room.id);
          const baseLabel = isCubicle ? room.id : isOffice ? room.id : room.name.split(' ')[0];
          const label = hasUltrawide ? `${baseLabel}*` : baseLabel;
          const sublabel = isCubicle ? '' : room.type.charAt(0).toUpperCase() + room.type.slice(1);

          return (
            <g
              key={room.id}
              className="room-group"
              onClick={() => !isBooked && onRoomSelect?.(room.id)}
              onMouseEnter={() => setHoveredRoom(room.id)}
              onMouseLeave={() => setHoveredRoom(null)}
              style={{ cursor: isBooked ? 'not-allowed' : 'pointer' }}
              role="button"
              aria-label={`${room.name} - ${isBooked ? 'Booked' : 'Available'}`}
              tabIndex={0}
            >
              <rect
                x={f.x} y={f.y} width={f.w} height={f.h}
                fill={getRoomColor(room)}
                opacity={getRoomOpacity(room)}
                stroke={selectedRoom === room.id ? '#fff' : (hasFloorplanImage ? getRoomColor(room) : '#333')}
                strokeWidth={selectedRoom === room.id ? 3 : (hasFloorplanImage ? 2.5 : 1)}
                strokeOpacity={hasFloorplanImage ? 0.9 : 1}
                rx={isCubicle ? 4 : 6}
              />
              <text
                x={f.x + f.w / 2}
                y={f.y + f.h / 2 + (sublabel ? -6 : 0)}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={fontSize}
                fontWeight="600"
                fill="#fff"
                pointerEvents="none"
              >
                {label}
              </text>
              {sublabel && (
                <text
                  x={f.x + f.w / 2}
                  y={f.y + f.h / 2 + 10}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="10"
                  fill="#fff"
                  opacity="0.85"
                  pointerEvents="none"
                >
                  {sublabel}
                </text>
              )}
              {isBooked && (
                <text
                  x={f.x + f.w / 2}
                  y={f.y + f.h - (isCubicle ? 8 : 14)}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#fff"
                  opacity="0.9"
                  pointerEvents="none"
                >
                  {availability[room.id]?.bookedBy || 'Reserved'}
                </text>
              )}
            </g>
          );
        })}

        {/* Non-bookable rooms (white fill, thin black border) */}
        {rooms.filter(r => r.bookable === false).map((room) => {
          const f = room.floor;
          const hasAssignee = !!room.assignedTo;
          // Assigned rooms: name on top, title below
          // Utility rooms: just the room name centered
          return (
            <g key={room.id} style={{ cursor: 'default' }}>
              <rect
                x={f.x} y={f.y} width={f.w} height={f.h}
                fill="#fff"
                opacity={0.95}
                stroke="#111"
                strokeWidth={1}
                rx={6}
              />
              {hasAssignee ? (
                <>
                  <text
                    x={f.x + f.w / 2}
                    y={f.y + f.h / 2 - 8}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="11"
                    fontWeight="600"
                    fill="#333"
                    pointerEvents="none"
                  >
                    {room.assignedTo}
                  </text>
                  <text
                    x={f.x + f.w / 2}
                    y={f.y + f.h / 2 + 8}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize="9"
                    fill="#555"
                    pointerEvents="none"
                  >
                    {room.mapLabel || room.name}
                  </text>
                </>
              ) : (
                <text
                  x={f.x + f.w / 2}
                  y={f.y + f.h / 2}
                  textAnchor="middle"
                  dominantBaseline="central"
                  fontSize="11"
                  fontWeight="600"
                  fill="#333"
                  pointerEvents="none"
                >
                  {room.name}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      <div className="floorplan-footnote">* Equipped with ultra-widescreen monitor</div>

      {isKiosk && <GuestWifiButton />}
    </div>
  );
}

function GuestWifiButton() {
  const [showWifi, setShowWifi] = useState(false);

  useEffect(() => {
    if (!showWifi) return;
    const timer = setTimeout(() => setShowWifi(false), 10000);
    return () => clearTimeout(timer);
  }, [showWifi]);

  return (
    <>
      <button
        className="guest-wifi-btn"
        onClick={() => setShowWifi(true)}
      >
        Click Here for Guest WiFi
      </button>

      {showWifi && (
        <div className="wifi-overlay" onClick={() => setShowWifi(false)}>
          <div className="wifi-popup" onClick={(e) => e.stopPropagation()}>
            <h3>Guest WiFi</h3>
            <div className="wifi-detail">
              <span className="wifi-label">Network</span>
              <span className="wifi-value">SSPGUEST</span>
            </div>
            <div className="wifi-detail">
              <span className="wifi-label">Password</span>
              <span className="wifi-value">sspismovingonup</span>
            </div>
            <div className="wifi-countdown">This will close automatically</div>
          </div>
        </div>
      )}
    </>
  );
}
